import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { useStepsContext } from '../../Hooks/useStepsContext';
import { determineCurrency } from '../../Utils/functions';
import { laneCopy } from '../../Utils/laneCopy';
import { resolveIncomeLane } from '../../Utils/incomeLane';
import AlertComponent from '../Alert/AlertComponent';
import BotResponse from '../BotResponse/BotResponse';
import Button from '../Button/Button';
import RadioOption from '../RadioOption/RadioOption';
import classes from 'classnames';

export default function LowIncomeDisclaimerAgreement() {
  const { state, setState, next, back, setShouldSave, error, setError, animate, makeFakeLoading, fakeLoadingDuration, stepPassed } =
    useStepsContext();

  const copy = laneCopy.borderline;
  const isTestLead = state.name.toLowerCase().includes('test');

  // This screen had no analytics at all, so nobody knew how many people saw it or declined.
  //
  // The ref keeps the view event to exactly one push. Without it the effect fires twice under
  // React StrictMode in development, and would fire again on any real remount in production
  // (a key change, a parent remount), inflating the count. The ref survives StrictMode's
  // setup/cleanup/setup cycle, so it is the guard that makes this idempotent.
  const viewFired = useRef(false);
  useEffect(() => {
    if (stepPassed || isTestLead || viewFired.current) return;
    viewFired.current = true;

    TagManager.dataLayer({
      dataLayer: {
        event: 'allybot2_lane_disclosure_viewed',
        allybot2_income_lane: resolveIncomeLane(state.gender, state.income),
        allybot2_disclosure_copy_id: copy.copyId,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDisclaimerAgreementChange = (e) => {
    setShouldSave(true);
    setState((state) => ({ ...state, lowIncomeDisclaimerAgreement: e.target.value }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    makeFakeLoading();

    setTimeout(() => {
      if (!state.lowIncomeDisclaimerAgreement) return setError('error');

      if (!isTestLead) {
        TagManager.dataLayer({
          dataLayer: {
            event:
              state.lowIncomeDisclaimerAgreement === 'yes'
                ? 'allybot2_lane_disclosure_continued'
                : 'allybot2_lane_disclosure_declined',
            allybot2_income_lane: resolveIncomeLane(state.gender, state.income),
            allybot2_disclosure_copy_id: copy.copyId,
          },
        });
      }

      next();
    }, fakeLoadingDuration);
  };

  // add converted currency for Canada, Australia and UK
  const currency = determineCurrency(state.address);
  const getCurrencyString = () => {
    switch (currency) {
      case 'cad':
        return ' (~$2,400 CAD)';
      case 'aud':
        return ' (~$2,400 AUD)';
      case 'gbp':
        return ' (~£1,300)';
      default:
        return '';
    }
  };

  return (
    <form name="disclaimer" onSubmit={handleNext}>
      {error && <AlertComponent text="There was a problem with your submission. Errors are marked below." />}

      {/* The price line is Allybot-local and stays in this component together with its currency
          machinery (OD-15): Strapi shows the same disclosure sentence below but does not localise a
          price, so no figure may travel inside the shared string. */}
      <BotResponse className={classes(animate(0), 'mb-20px')}>
        Before we book you in, a quick word on cost: VIDA starts at{' '}
        <strong>$1,695{getCurrencyString()} a month</strong>, one month at a time. 4 out of 5 clients
        meet someone within 3 months.
      </BotResponse>

      {/* The shared sentence, rendered from configuration. Carries no amount and no currency symbol. */}
      {copy.body.map((line, i) => (
        <BotResponse key={line} className={classes(animate(i + 1), 'mb-20px')}>
          {line}
        </BotResponse>
      ))}

      <div className={animate(copy.body.length + 1)}>
        <BotResponse className="mb-20px">{copy.question}</BotResponse>

        <div className="btn-group-toggle" data-toggle="buttons">
          <RadioOption
            id="agree"
            name="disclaimer"
            value="yes"
            label={copy.optionLabels.yes}
            checked={state.lowIncomeDisclaimerAgreement === 'yes'}
            onChange={handleDisclaimerAgreementChange}
            hasError={!!error}
          />
          <RadioOption
            id="disagree"
            name="disclaimer"
            value="no"
            label={copy.optionLabels.no}
            checked={state.lowIncomeDisclaimerAgreement === 'no'}
            onChange={handleDisclaimerAgreementChange}
            hasError={!!error}
          />
        </div>
      </div>

      {error && <p className="has-error">You must select one of the options above.</p>}

      <div className="frm_submit">
        <Button label="Back" onClick={back} />
        <Button label="Next" onClick={handleNext} />
      </div>
    </form>
  );
}
